<template>
  <div class="agent-items-table">
    <div class="d-flex align-center justify-space-between mb-2">
      <span class="agent-items-table__title">{{ title }}</span>
      <v-btn
        size="x-small"
        variant="tonal"
        color="primary"
        prepend-icon="mdi-plus"
        class="text-none"
        rounded="lg"
        :disabled="disabled"
        @click="addRow"
      >
        {{ addLabel }}
      </v-btn>
    </div>

    <div
      v-if="!localItems.length"
      class="agent-items-table__placeholder rounded-lg text-center pa-4"
    >
      <p class="mb-0 text-medium-emphasis">{{ emptyText }}</p>
    </div>

    <div v-else-if="outlinedValues" class="agent-items-table__list">
      <div
        v-for="(row, index) in localItems"
        :key="rowKey(row, index)"
        class="agent-items-table__list-row d-flex align-start ga-2"
      >
        <v-text-field
          :model-value="row.item_text"
          :prepend-inner-icon="prependValueIcon"
          :type="valueInputType"
          variant="outlined"
          density="compact"
          hide-details="auto"
          rounded="lg"
          class="agent-items-table__field flex-grow-1"
          :placeholder="valuePlaceholder"
          :rules="valueRules"
          :disabled="disabled"
          @update:model-value="patchRow(index, 'item_text', $event)"
        />
        <v-btn
          icon
          size="small"
          variant="text"
          color="error"
          class="agent-items-table__delete flex-shrink-0 mt-1"
          :disabled="disabled || localItems.length <= minRows"
          @click="removeRow(index)"
        >
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <v-table
      v-else
      density="compact"
      class="agent-items-table__table rounded-lg"
      :class="{
        'agent-items-table__table--mixed-layout': showTopicType,
        'agent-items-table__table--question-layout':
          !showTopicType && compactParameterColumn,
      }"
    >
      <thead>
        <tr>
          <th class="agent-items-table__col-seq">#</th>
          <th v-if="showTopicType" class="agent-items-table__col-type">Type</th>
          <th
            v-if="showParameterColumn"
            class="agent-items-table__col-name"
            :class="{
              'agent-items-table__col-name--compact':
                !showTopicType && compactParameterColumn,
            }"
          >
            Parameter
          </th>
          <th
            v-if="showRequiredColumn"
            class="agent-items-table__col-req text-center"
          >
            Required
          </th>
          <th class="agent-items-table__col-value">{{ valueColumnLabel }}</th>
          <th class="agent-items-table__col-actions text-end" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in localItems" :key="rowKey(row, index)">
          <td class="text-medium-emphasis">{{ row.item_sequence }}</td>
          <td v-if="showTopicType" class="agent-items-table__col-type">
            <v-select
              :model-value="row.topic_type"
              :items="topicTypeOptions"
              item-title="label"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
              rounded="lg"
              class="agent-items-table__cell-field agent-items-table__type-select"
              :disabled="disabled"
              @update:model-value="onTopicTypeChange(index, $event)"
            />
          </td>
          <td
            v-if="showParameterColumn"
            :class="{
              'agent-items-table__col-name--compact':
                !showTopicType && compactParameterColumn,
            }"
          >
            <v-text-field
              v-if="isQuestionRow(row)"
              :model-value="row.parameter_name"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="Parameter name"
              rounded="lg"
              class="agent-items-table__cell-field"
              :disabled="disabled"
              @update:model-value="patchRow(index, 'parameter_name', $event)"
            />
            <span v-else class="text-medium-emphasis agent-items-table__na"
              >—</span
            >
          </td>
          <td v-if="showRequiredColumn" class="text-center">
            <v-checkbox-btn
              v-if="isQuestionRow(row)"
              :model-value="!!row.required"
              density="compact"
              hide-details
              color="primary"
              :disabled="disabled"
              @update:model-value="patchRow(index, 'required', $event)"
            />
            <span v-else class="text-medium-emphasis agent-items-table__na"
              >—</span
            >
          </td>
          <td class="agent-items-table__col-value">
            <v-text-field
              :model-value="row.item_text"
              density="compact"
              variant="outlined"
              hide-details="auto"
              rounded="lg"
              class="agent-items-table__cell-field"
              :placeholder="valuePlaceholderForRow(row)"
              :rules="valueRules"
              :disabled="disabled"
              @update:model-value="patchRow(index, 'item_text', $event)"
            />
          </td>
          <td class="text-end">
            <v-btn
              icon
              size="x-small"
              variant="text"
              color="error"
              :disabled="disabled || localItems.length <= minRows"
              @click="removeRow(index)"
            >
              <v-icon size="18">mdi-delete-outline</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-table>
  </div>
</template>

<script>
import {
  CONVERSATION_BLOCK_TYPE_OPTIONS,
  emptyInformationInputRow,
  isQuestionBlockType,
  normalizeInformationInputItem,
  resequenceInformationItems,
} from '@/utils/agentConfiguration'

export default {
  name: 'AgentItemsTable',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      required: true,
    },
    addLabel: {
      type: String,
      default: 'Add row',
    },
    emptyText: {
      type: String,
      default: 'No rows yet. Add a row to get started.',
    },
    valueColumnLabel: {
      type: String,
      default: 'Text',
    },
    valuePlaceholder: {
      type: String,
      default: 'Enter value',
    },
    valueInputType: {
      type: String,
      default: 'text',
    },
    prependValueIcon: {
      type: String,
      default: '',
    },
    valueRules: {
      type: Array,
      default: () => [],
    },
    outlinedValues: {
      type: Boolean,
      default: false,
    },
    showTopicType: {
      type: Boolean,
      default: false,
    },
    showParameterName: {
      type: Boolean,
      default: true,
    },
    showRequired: {
      type: Boolean,
      default: true,
    },
    compactParameterColumn: {
      type: Boolean,
      default: false,
    },
    minRows: {
      type: Number,
      default: 0,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      topicTypeOptions: CONVERSATION_BLOCK_TYPE_OPTIONS,
    }
  },
  computed: {
    localItems() {
      const rows = Array.isArray(this.modelValue) ? this.modelValue : []
      return rows.map((row, index) => normalizeInformationInputItem(row, index))
    },
    showParameterColumn() {
      return this.showTopicType || this.showParameterName
    },
    showRequiredColumn() {
      return this.showTopicType || this.showRequired
    },
  },
  methods: {
    rowKey(row, index) {
      return `${row.item_sequence}-${index}`
    },
    isQuestionRow(row) {
      return isQuestionBlockType(row.topic_type)
    },
    valuePlaceholderForRow(row) {
      return this.isQuestionRow(row)
        ? 'Question text'
        : this.valuePlaceholder
    },
    emitItems(items) {
      this.$emit('update:modelValue', resequenceInformationItems(items))
    },
    patchRow(index, field, value) {
      const next = this.localItems.map((row, idx) =>
        idx === index ? { ...row, [field]: value } : { ...row },
      )
      this.emitItems(next)
    },
    onTopicTypeChange(index, topicType) {
      const row = this.localItems[index]
      const next = this.localItems.map((item, idx) => {
        if (idx !== index) return { ...item }
        const isQuestion = isQuestionBlockType(topicType)
        return {
          ...item,
          topic_type: topicType,
          parameter_name: isQuestion ? item.parameter_name : '',
          required: isQuestion ? item.required : false,
        }
      })
      this.emitItems(next)
    },
    addRow() {
      const next = [
        ...this.localItems,
        emptyInformationInputRow(this.localItems.length + 1),
      ]
      this.emitItems(next)
    },
    removeRow(index) {
      if (this.localItems.length <= this.minRows) return
      const next = this.localItems.filter((_, idx) => idx !== index)
      this.emitItems(next)
    },
  },
}
</script>

<style scoped>
.agent-items-table__title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--y-text-secondary);
}

.agent-items-table__placeholder {
  border: 1px dashed var(--y-glass-border);
  background: var(--y-panel-bg);
  font-size: 0.8125rem;
}

.agent-items-table__list {
  display: flex;
  flex-direction: column;
  gap: var(--y-space-2);
}

.agent-items-table__list-row {
  width: 100%;
}

.agent-items-table__field :deep(.v-field) {
  font-size: 0.8125rem;
}

.agent-items-table__table {
  border: 1px solid var(--y-glass-border);
  background: var(--y-panel-bg);
}

.agent-items-table__table :deep(th) {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--y-text-muted);
  white-space: nowrap;
  padding-left: 12px;
  padding-right: 12px;
}

.agent-items-table__table :deep(td) {
  vertical-align: middle;
  padding: 8px 12px;
}

.agent-items-table__cell-field {
  min-width: 0;
}

.agent-items-table__type-select {
  min-width: 120px;
}

.agent-items-table__na {
  display: inline-block;
  min-width: 24px;
  font-size: 0.8125rem;
}

.agent-items-table__col-seq {
  width: 40px;
}

.agent-items-table__col-type {
  width: 132px;
}

.agent-items-table__col-name {
  min-width: 140px;
}

.agent-items-table__table--question-layout {
  table-layout: fixed;
  width: 100%;
}

.agent-items-table__table--mixed-layout {
  table-layout: fixed;
  width: 100%;
}

.agent-items-table__col-name--compact {
  width: 18%;
  max-width: 110px;
}

.agent-items-table__table--question-layout .agent-items-table__col-value {
  width: 52%;
}

.agent-items-table__table--mixed-layout .agent-items-table__col-name {
  width: 22%;
  min-width: 150px;
}

.agent-items-table__table--mixed-layout .agent-items-table__col-value {
  width: 36%;
  min-width: 180px;
}

.agent-items-table__col-req {
  width: 72px;
}

.agent-items-table__col-actions {
  width: 48px;
}
</style>
